import React, {createContext, useState, useEffect} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Alert, Platform, ToastAndroid} from 'react-native';
import {getAccessToken} from './storage';
import Observables from "@happy-gastro/global-observables";
import Toast from "react-native-toast-message";
import ErrorReportingService from './ErrorReportingService';

Date.prototype.addHours = function (h) {
    this.setHours(this.getHours() + h);
    return this;
};

export const OrdersContext = createContext();

const OrdersProvider = ({children}) => {
    const [orders, setOrders] = useState([]);
    const [isConnected, setIsConnected] = useState(false); // Tracks network connectivity
    const [client, setClient] = useState(null); // MQTT or WebSocket client instance
    const [syncInterval, setSyncInterval] = useState(null); // Interval for offline sync
    const API_URL = 'https://api.happygastro.hu/api';

    // Helper function to filter order properties to only include those used in the app
    const filterOrderProperties = (order) => {

        return {
            _id: order._id,
            status: order.status,
            storno: order.storno,
            orderCode: order.orderCode,
            serviceFee: order.serviceFee,
            deliveryFee: order.deliveryFee,
            serviceFeePrice: order.serviceFeePrice,
            discountAmount: order.discountAmount,
            orderType: order.orderType,
            orderSum: order.orderSum,
            productsPrice: order.productsPrice,
            discounts: order.discounts,
            items: order.items ? order.items.map(item => ({
                _id: item._id,
                name: item.name,
                remoteName: item.remoteName,
                remoteCode: item.remoteCode,
                price: item.price,
                remotePrice: item.remotePrice,
                amount: item.amount,
                item: item.item,
                extras: item.extras ? item.extras.map(extra => ({
                    _id: extra._id,
                    item: extra.item,
                    name: extra.name,
                    price: extra.price,
                    remoteName: extra.remoteName,
                    remoteCode: extra.remoteCode,
                    remotePrice: extra.price
                })) : [],
                round: item.round,
                comment: item.comment,
                comments: item.comments,
                size: item.size,
                sizeIndex: item.sizeIndex
            })) : [],
            paymentType: order.paymentType,
            tableId: order.tableId,
            tableName: order.tableName,
            createdAt: order.createdAt,
            preOrder: order.preOrder,
            userInfo: order.userInfo,
            addressInfo: order.addressInfo,
            delivery: order.delivery,
            printed: order.printed,
            restaurantPlace: order.restaurantPlace,
            orderFrom: order.orderFrom,
            paymentTypes: order.paymentTypes,
            token: order.token,
            remoteId: order.remoteId,
            origin: order.origin,
            platformId: order.platformId,
            venueId: order.venueId,
            history: order.history ? order.history.filter(
                historyItem => historyItem && (historyItem.event === "removeItems" || historyItem.event === "payment")
            ) : [],
            events: order.events ? order.events.map(event => ({
                time: event.time,
                event: event.event,
                details: event.details,
                user: event.user
            })) : [],
        };
    };

    // Fetch orders from the API
    const fetchOrdersFromAPI = async () => {
        try {
            const token = await getAccessToken(); // Retrieve the token from AsyncStorage


            const restaurantData = await AsyncStorage.getItem("restaurant");
            const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
            const restaurant = JSON.parse(restaurantData);
            const place = JSON.parse(restaurantPlaceData);
            if(!token || !restaurantData) return;
            const URL_ENDING = '/restaurant/data/'+restaurant._id+'/orders?placeId='+place._id;

            const response = await fetch(API_URL + URL_ENDING, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Add the token to the Authorization header
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();

            // Filter order properties to only include those used in the app
            const filteredOrders = data.map(order => filterOrderProperties(order));

            setOrders(filteredOrders);
            await AsyncStorage.setItem('orders', JSON.stringify(filteredOrders)); // Cache orders offline

            // Check for online orders to notify user
            const onlineOrderName = (orderFrom) => {
                switch (orderFrom) {
                    case 'netpincer': return "Foodora";
                    case 'foodora': return "Foodora";
                    case 'foodpanda': return "Foodora";
                    case 'wolt': return "Wolt";
                    case 'webshop': return "Webshop";
                    default: return "Online";
                }
            }
            const pendingOnlineOrders = data.filter(order => (order.status === "pending" && (order.orderType === "online" || order.orderType === "takeAway"))) || [];
            if(pendingOnlineOrders.length > 0){
                if(Platform.OS === "android") {
                    const PushNotification = require("react-native-push-notification");
                    PushNotification.localNotification({
                        channelId: "pending-orders", // Ensure this matches your channel ID
                        title: pendingOnlineOrders.length + " függőben lévő rendelés", // Notification title
                        message: "Rendelés várakozik! Kattints ide, és fogadd el gyorsan, hogy az ügyfél elégedett legyen.", // Notification message
                    });
                }
                pendingOnlineOrders.forEach((order, index) => {
                    setTimeout(() => {
                        Toast.show({
                            id: order._id,
                            type: 'pendingOrderToast2',
                            position: 'bottom',
                            bottomOffset: 100,
                            autoHide: false,
                            props: { ...order, message: "Függőben lévő "+onlineOrderName(order.orderFrom)+" rendelés: #"+order.orderCode }
                        });
                    }, (index * 2000))
                })
            }
        } catch (error) {
            //loadOrdersFromStorage(); // Load from storage if the API fails
        }
    };

    const fetchOrdersByDate = async (startDate, endDate) => {
        const startDay = new Date(startDate.setHours(0, 0, 1, 0));
        const endDay = new Date(endDate.setHours(23, 59, 59, 1));
        try {
            const token = await getAccessToken(); // Retrieve the token from AsyncStorage
            const restaurantData = await AsyncStorage.getItem("restaurant");
            const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
            if(!token || !restaurantData) return;

            const URL_ENDING = '/restaurant/data/orders?startDate=' + startDay.toISOString() + '&endDate=' + endDay.toISOString();

            const response = await fetch(API_URL + URL_ENDING, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Add the token to the Authorization header
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();

            // Filter order properties to only include those used in the app
            return data.map(order => filterOrderProperties(order));
        } catch (error) {
            //loadOrdersFromStorage(); // Load from storage if the API fails
        }
    };

    const storeOrder = async (orderData) => {
        const token = await getAccessToken()

        //setOrders([...orders, orderData]);
        //AsyncStorage.setItem('orders', JSON.stringify([...orders, orderData]));

        return fetch(API_URL + '/order/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer '+token },
            body: JSON.stringify(orderData),
        })
            .then(response => response.json())
            .then(data => {
                // Filter order properties to only include those used in the app
                const filteredOrder = filterOrderProperties(data);
                setOrders([...orders, filteredOrder]);
                //AsyncStorage.setItem('orders', JSON.stringify([...orders, filteredOrder]));
                return data;
            })
            .catch(error => {
                console.error("Failed to store order:", error);
                //storeOrderOffline(orderData); // Store offline if API fails
                return false
            });
    }

    // Load orders from AsyncStorage (offline cache)
    const loadOrdersFromStorage = async () => {
        try {
            const storedOrders = await AsyncStorage.getItem('orders');
            if (storedOrders) setOrders(JSON.parse(storedOrders));
        } catch (error) {
            console.error("Failed to load orders from storage:", error);
        }
    };

    // Store new orders locally for later sync
    const storeOrderOffline = async (order) => {
        try {
            const storedOrders = await AsyncStorage.getItem('offlineOrders');
            const offlineOrders = storedOrders ? JSON.parse(storedOrders) : [];
            offlineOrders.push(order);
            await AsyncStorage.setItem('offlineOrders', JSON.stringify(offlineOrders));
        } catch (error) {
            console.error("Failed to store order offline:", error);
        }
    };

    // Sync offline orders with the API
    const syncOrdersWithAPI = async () => {
        /*
        try {
            const storedOrders = await AsyncStorage.getItem('offlineOrders');
            if (!storedOrders) return;

            const offlineOrders = JSON.parse(storedOrders);
            for (const order of offlineOrders) {
                await fetch(API_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(order),
                });
            }

            await AsyncStorage.removeItem('offlineOrders'); // Clear synced orders
        } catch (error) {
            console.error("Failed to sync orders with API:", error);
        }
         */
    };

    // MQTT/WebSocket setup
    const setupRealTimeUpdates = () => {
        try {
            // Fallback to periodic API polling every 10 seconds
            const interval = setInterval(fetchOrdersFromAPI, 10 * 1000);
            setSyncInterval(interval);
        } catch (error) {
            ErrorReportingService.error("Failed to setup real-time updates", { error }, "OrdersContext");
        }
    };

    useEffect(() => {
        fetchOrdersFromAPI(); // Fetch orders on component mount
        setupRealTimeUpdates(); // Setup real-time updates
        Observables.register("reloadOrders", fetchOrdersFromAPI);
        return () => {
            clearInterval(syncInterval);
            setSyncInterval(null);
        } // Cleanup on unmount
    }, []);

    return (
        <OrdersContext.Provider value={{orders, storeOrderOffline, storeOrder, fetchOrdersFromAPI, fetchOrdersByDate}}>
            {children}
        </OrdersContext.Provider>
    );
};
export default React.memo(OrdersProvider)
